import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Card, Row, Col, Form, Alert, Spinner } from 'react-bootstrap';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiArrowLeft } from 'react-icons/fi';
import axiosInstance from '../../services/AxiosInstance';
import {
  createCreditCheckout,
  previewCreditTopup,
  getSavedCreditCheckoutCredits,
  saveCreditCheckoutCredits,
  clearCreditCheckoutStorage,
  creditsWalletPath,
} from '../../services/CreditService';
import {
  getPendingCheckoutId,
  setPaymenntCheckoutSession,
  clearPaymenntCheckoutSession,
  stripCheckoutQueryParams,
} from '../../utils/paymentCheckoutParams';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import '../tasks/tasklist.css';
import '../users/userlist.css';
import './SubscriptionCheckout.css';
import visaCardLogo from '../../icons/cards/visa.png';
import masterCardLogo from '../../icons/cards/master card.png';

export default function CreditCheckout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const creditsParam = searchParams.get('credits');
  const checkoutIdParam = getPendingCheckoutId(searchParams);

  const [credits, setCredits] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewError, setPreviewError] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [checkingReturn, setCheckingReturn] = useState(false);

  useEffect(() => {
    const fromUrl = creditsParam != null ? Number(creditsParam) : null;
    const fromStorage = getSavedCreditCheckoutCredits();
    const amount =
      fromUrl != null && Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : fromStorage;

    if (amount == null || !Number.isFinite(amount) || amount <= 0) {
      setCredits(null);
      setLoading(false);
      return;
    }

    setCredits(amount);
    saveCreditCheckoutCredits(amount);

    let cancelled = false;
    (async () => {
      setLoading(true);
      setPreviewError(null);
      try {
        const data = await previewCreditTopup(amount);
        if (cancelled) return;
        if (data?.success) {
          setPreview(data);
          setPreviewError(null);
        } else {
          setPreview(null);
          setPreviewError(data?.message || 'Failed to load checkout preview');
        }
      } catch (err) {
        if (cancelled) return;
        setPreview(null);
        setPreviewError(err.response?.data?.message || 'Failed to load checkout preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [creditsParam]);

  useEffect(() => {
    if (!checkoutIdParam) return undefined;

    const checkStatus = async () => {
      setCheckingReturn(true);
      setPaymentError(null);
      try {
        const response = await axiosInstance.get(`/payment/status/${checkoutIdParam}`);
        const { status, failureReason } = response.data;

        if (status === 'completed' || status === 'paid') {
          clearCreditCheckoutStorage();
          clearPaymenntCheckoutSession();
          navigate(`/subscription/payment-success?checkout=${encodeURIComponent(checkoutIdParam)}`, {
            replace: true,
          });
          return;
        }

        if (status === 'failed' || status === 'cancelled' || status === 'refunded') {
          clearPaymenntCheckoutSession();
          setPaymentError(failureReason || 'Payment could not be completed. Please try again.');
        } else {
          setTimeout(() => checkStatus(), 3000);
        }
      } catch (err) {
        const statusCode = err.response?.status;
        if (statusCode === 404) {
          clearPaymenntCheckoutSession();
          const nextSearch = stripCheckoutQueryParams(location.search);
          if (nextSearch !== (location.search || '')) {
            navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
          }
          return;
        }
        setPaymentError('We are verifying your payment. Please wait or try again.');
        setTimeout(() => checkStatus(), 5000);
      } finally {
        setCheckingReturn(false);
      }
    };

    checkStatus();
    return undefined;
  }, [checkoutIdParam, navigate, location.pathname, location.search]);

  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) {
        setIsPaying(false);
        setCheckingReturn(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(creditsWalletPath());
    }
  };

  const handlePayNow = async () => {
    if (!credits || !termsAccepted) return;

    setIsPaying(true);
    setPaymentError(null);
    try {
      const data = await createCreditCheckout(credits);
      if (data?.success && data.redirectUrl) {
        setPaymenntCheckoutSession(data.checkoutId || '');
        window.location.href = data.redirectUrl;
        return;
      }
      setPaymentError(data?.message || 'Failed to create checkout');
      setIsPaying(false);
    } catch (err) {
      setPaymentError(err.response?.data?.message || 'Failed to start payment. Please try again.');
      setIsPaying(false);
    }
  };

  const amountUsd = preview != null ? Number(preview.amount_usd ?? preview.amount ?? 0) : 0;
  const amountAed = preview != null ? Number(preview.amount_aed ?? 0) : null;
  const tax = 0;
  const serviceCharge = 0;
  const total = amountUsd + tax + serviceCharge;

  if (loading && !checkoutIdParam) {
    return (
      <>
        <Row style={{ marginBottom: '0px !important' }}>
          <Col lg={12}>
            <Card className="user-list-card">
              <Card.Body style={{ minHeight: '400px', padding: '1.5rem' }}>
                <div className="checkout-loading">
                  <Spinner animation="border" style={{ color: '#C42126' }} />
                  <p className="mt-3 text-muted">Loading checkout...</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <InternalFooter />
      </>
    );
  }

  if (!credits && !checkoutIdParam) {
    return (
      <>
        <button type="button" className="checkout-back-button" onClick={handleBack}>
          <FiArrowLeft style={{ marginRight: '0.5rem' }} />
          Back
        </button>
        <Row style={{ marginBottom: '0px !important' }}>
          <Col lg={12}>
            <Card className="user-list-card">
              <Card.Body className="text-center p-5">
                <Alert variant="warning" className="checkout-alert">
                  No credit amount selected. Please choose an amount to top up first.
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <InternalFooter />
      </>
    );
  }

  return (
    <>
      <button type="button" className="checkout-back-button" onClick={handleBack}>
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <Row style={{ marginBottom: '0px !important' }}>
        <ToastContainer position="top-right" autoClose={5000} />
        <Col lg={12}>
          <Card className="user-list-card">
            <Card.Body style={{ minHeight: '400px', overflow: 'visible', padding: '1.5rem' }}>
              {paymentError && (
                <Alert
                  variant="danger"
                  className="checkout-alert mb-4"
                  dismissible
                  onClose={() => setPaymentError(null)}
                >
                  <strong>We could not process the payment due to the following error. Please try again.</strong>
                  <p className="mb-0 mt-2 fw-bold">{paymentError}</p>
                </Alert>
              )}

              {previewError && (
                <Alert variant="warning" className="checkout-alert mb-4">
                  {previewError}
                </Alert>
              )}

              <Row className="align-items-start">
                <Col lg={6} className="mb-4">
                  <Card className="checkout-detail-card">
                    <Card.Header className="checkout-card-header checkout-detail-card-header">
                      <h5 className="mb-0">Credit Top-up Details</h5>
                    </Card.Header>
                    <Card.Body>
                      <div className="checkout-detail-rows">
                        <div className="checkout-detail-row">
                          <span className="text-muted">Purchase type</span>
                          <span className="fw-semibold checkout-detail-value">Credit wallet top-up</span>
                        </div>
                        <div className="checkout-detail-row">
                          <span className="text-muted">Credits</span>
                          <span className="fw-semibold checkout-detail-value">
                            ${Number(preview?.credits ?? credits ?? 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="checkout-detail-row checkout-detail-row-total">
                          <span className="fw-bold">Total (USD)</span>
                          <span className="fw-bold checkout-detail-value">
                            {loading ? '…' : `$${total.toFixed(2)}`}
                          </span>
                        </div>
                      </div>

                      <div className="checkout-renewal-notice mt-4">
                        <i className="fas fa-info-circle me-2" aria-hidden="true" />
                        Credits are added to your company wallet and can be used by any subscribed module.
                      </div>

                      <div className="payment-logos mt-4">
                        <img src={visaCardLogo} alt="Visa" className="checkout-card-logo" />
                        <img src={masterCardLogo} alt="Mastercard" className="checkout-card-logo" />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={6} className="mb-4">
                  <Card className="checkout-payment-card">
                    <Card.Header className="checkout-card-header">
                      <h5 className="mb-0">Payment Summary</h5>
                    </Card.Header>
                    <Card.Body>
                      <div className="payment-summary-row">
                        <span className="text-muted">Credit top-up charge</span>
                        <span>{loading ? '…' : `$${amountUsd.toFixed(2)} USD`}</span>
                      </div>
                      <div className="payment-summary-row">
                        <span className="text-muted">Tax</span>
                        <span>${tax.toFixed(2)} USD</span>
                      </div>
                      <div className="payment-summary-row">
                        <span className="text-muted">Service Charge</span>
                        <span>${serviceCharge.toFixed(2)} USD</span>
                      </div>
                      <div className="payment-summary-row payment-total">
                        <span className="fw-bold">Totals</span>
                        <span className="fw-bold">{loading ? '…' : `$${total.toFixed(2)} USD`}</span>
                      </div>

                      <Form.Group className="mb-3 mt-4">
                        <Form.Check
                          type="checkbox"
                          id="credit-terms-check"
                          label={
                            <>
                              I confirm that I have read the{' '}
                              <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: '#C42126' }}>
                                Terms &amp; Conditions
                              </Link>
                              {' '}and{' '}
                              <Link to="/refund-cancellation-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#C42126' }}>
                                Refund &amp; Cancellation Policy
                              </Link>
                            </>
                          }
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          className="checkout-terms-check"
                        />
                      </Form.Group>

                      <button
                        type="button"
                        className="new-user-btn w-100 pay-now-btn checkout-pay-btn"
                        onClick={handlePayNow}
                        disabled={
                          !termsAccepted ||
                          isPaying ||
                          checkingReturn ||
                          loading ||
                          !!previewError ||
                          !preview
                        }
                        style={{
                          border: 'none',
                          cursor:
                            !termsAccepted || isPaying || checkingReturn || loading || previewError || !preview
                              ? 'not-allowed'
                              : 'pointer',
                          opacity:
                            !termsAccepted || isPaying || checkingReturn || loading || previewError || !preview
                              ? 0.7
                              : 1,
                        }}
                      >
                        {isPaying ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Redirecting to payment...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-credit-card me-2" aria-hidden="true" />
                            Pay Now
                          </>
                        )}
                      </button>

                      <p className="checkout-redirect-msg mt-3 mb-2">
                        You will be redirected to the Payment Gateway portal for making the payment using Credit/Debit card.
                        The payment will reflect in <strong>AED</strong> (
                        <strong>
                          {loading ? '…' : amountAed != null ? amountAed.toFixed(2) : '—'}
                        </strong>
                        ).
                      </p>
                      <p className="checkout-statement-msg text-muted small">
                        The payment will reflect in your card statement as &quot;Innate Technologies LLC-FZ&quot;, which is the parent company of flowdo.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <InternalFooter />
    </>
  );
}
